'use client'

import { styled } from 'styled-components'

import { Link } from '@b-stock/fe-core/bstock-react'
import {
  Breakpoints,
  Colors,
  Shadows,
  Typography,
} from '@b-stock/fe-core/design-system'

import Image from '@/components/Image'

export const Section = styled.section<{ $background?: string }>`
  display: flex;
  justify-content: center;
  width: 100%;
  background: ${({ $background }) => $background ?? 'transparent'};
`

export const Inner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  max-width: var(--maxPageWidth);
  padding: 3rem 1.5rem;

  @media ${Breakpoints.min.medium} {
    padding: 3rem;
  }
`

export const SectionTitle = styled.h2`
  ${Typography.Title2};
  margin: 0;
  color: ${Colors.Semantic.Neutral.light_black};
`

export const SectionBody = styled.p`
  ${Typography.Body1};
  margin: 0;
  text-wrap: pretty;
  color: ${Colors.Semantic.Neutral.dark_grey};
`

export const NotOnPhone = styled.span`
  display: none;

  @media ${Breakpoints.min.medium} {
    display: inline;
  }
`

export const TextLink = styled(Link)`
  ${Typography.Body2Semibold};
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`

export const TileGrid = styled.ul<{ $phoneColumns: 1 | 2 }>`
  display: grid;
  grid-template-columns: repeat(
    ${({ $phoneColumns }) => $phoneColumns},
    minmax(0, 1fr)
  );
  gap: 1rem;
  margin: 0;
  padding: 0;
  list-style: none;

  @media ${Breakpoints.min.medium} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (min-width: 80rem) {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
`

const tilePadding = '1.25rem'
const tileRadius = '0.5rem'
const tileBorder = '0.0625rem'

export const sellerLogoBox = { width: 96, height: 36 }

export const Tile = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  height: 100%;
  padding: ${tilePadding};
  border: ${tileBorder} solid ${Colors.Semantic.Neutral.medium_grey};
  border-radius: ${tileRadius};
  color: ${Colors.Semantic.Neutral.light_black};
  text-decoration: none;

  &:hover {
    border-color: ${Colors.Semantic.BStock.onhover};
    text-decoration: none;
  }
`

export const SellerTile = styled(Tile)`
  flex-direction: row;
  align-items: center;
  padding: 0.75rem 1rem;
  border-color: transparent;
  background: ${Colors.Semantic.Neutral.white};
  ${Shadows.MultipleCards};
`

export const SellerTileText = styled.span`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
`

export const TileChevron = styled.span`
  color: ${Colors.Semantic.Neutral.dark_grey};
`

export const SellerLogo = styled(Image)`
  flex-shrink: 0;
  width: ${sellerLogoBox.width / 16}rem;
  height: ${sellerLogoBox.height / 16}rem;
  object-fit: contain;
  object-position: left center;
`

export const CategoryPhoto = styled(Image)`
  display: block;
  width: calc(100% + 2 * ${tilePadding});
  max-width: none;
  height: auto;
  margin: calc(-1 * ${tilePadding}) calc(-1 * ${tilePadding}) 0;
  aspect-ratio: 800 / 451;
  object-fit: cover;
  border-radius: calc(${tileRadius} - ${tileBorder})
    calc(${tileRadius} - ${tileBorder}) 0 0;
  background: ${Colors.Semantic.Neutral.light_grey};
`

export const TileName = styled.span`
  ${Typography.Subtitle1};
  color: ${Colors.Semantic.Neutral.light_black};
`

export const TileMeta = styled.span`
  ${Typography.Body3};
  color: ${Colors.Semantic.Neutral.dark_grey};
`
